import { createSelector, createSlice } from '@reduxjs/toolkit'
import type {
  MetricUserJoinAs,
  NodePropertyName,
  ViewConfig,
  ViewConfigAdditionalConfig,
  ViewConfigKpi,
  ViewConfigKpiMeasure,
  ViewConfigSheet,
} from '@salescore/core'
import { z } from 'zod'

export interface KpiFormMetricFormValue {
  sheet?: ViewConfigSheet
  measure?: Partial<ViewConfigKpiMeasure>
  date?: { property: NodePropertyName }
  user?: { property?: NodePropertyName; joinAs?: MetricUserJoinAs }
}

// feature-view 側の KpiForm と同じ shape を保つ。
// filterFormItemCacheKey などの FE 専用フィールドを含むため core に上げず、両 FE パッケージで shape を揃える。
type KpiForm = Partial<Omit<ViewConfigKpi, 'sheet' | 'measure' | 'date' | 'user'>> & {
  name?: string
  viewGroupId?: string | null
  // eslint-disable-next-line sonarjs/todo-tag
  dashboardIds?: string[] // TODO 初期化
  metricFormValue?: KpiFormMetricFormValue
  // 基本的にこのKpiFormの状態=UIの状態になっているが、
  // 絞り込みのUIのみ、他の箇所でも使い回すetcのため、絞り込みコンポーネント内部で独自の状態を持っている。
  // この絞り込みUI以外の箇所から状態を変更した時、コンポーネント内部の状態を更新する必要があるので、
  // これを検出するために以下のキャッシュキーを使っている（詳細はこの呼び出し元であるKpiFormFilterTreeFormItem側を参照）
  filterFormItemCacheKey?: string
}

export const viewRecordFieldChangeSchema = z.object({
  rootNodeId: z.string().optional(),
  id: z.string().optional(),
  type: z.literal('fieldChange').optional(),
  streamName: z.string(),
  fieldChanges: z.array(
    z.object({
      id: z.string(),
      fieldName: z.string(),
      propertySourceName: z.string(),
      value: z.unknown(),
      valueLabel: z.string().optional(),
      rowIndex: z.number(),
      innerRowIndex: z.number(),
    }),
  ),
  recordNameLabel: z.string().optional(),
  before: z.record(z.string(), z.unknown()).optional(),
  after: z.record(z.string(), z.unknown()).optional(),
  deleted: z.boolean().optional(),
})

export type ViewRecordFieldChange = z.infer<typeof viewRecordFieldChangeSchema>

export interface ChangeHistory {
  datetime: string
  rowIndices: number[] // 最終的にこれがある行のみを更新・差分検出する予定（必要ないか？）
  snapshot: string // JSON形式で保存。変化の前の値
  snapshotAfterChange: string // 変化の後の値
  changes: ViewRecordFieldChange[]
}

export interface CachedView {
  viewId: string
  config?: ViewConfig
  changes?: ChangeHistory[]
  additionalConfig?: ViewConfigAdditionalConfig
  kpiPivotPickedPresetName?: string
  sheetPickedPresetName?: string
  kpiForm?: KpiForm
  isModifiedKpiForm?: boolean
  oldestSyncedAt?: string
  hasChangeToViewSchema?: boolean
  hasChangeToKpiPivotParameter?: boolean
  // シートを保存してタブを移動した時など、次回のrefetchでレコードのキャッシュを使わないためのフラグ
  shouldIgnoreRefetchCache?: boolean
}

export interface CachedViewsSlice {
  cachedViews: CachedView[]
}

const initialState: CachedViewsSlice = {
  cachedViews: [],
}

export const cachedViews = createSlice({
  name: 'cachedViews',
  initialState,
  reducers: {
    // cacheableViewIds としてタブの ViewID を与える、 useUpsertCachedView からのみ呼び出される想定
    _upsertCachedView: (
      { cachedViews }: CachedViewsSlice,
      {
        payload: { overridingCachedView, cacheableViewIds },
      }: { payload: { overridingCachedView: CachedView; cacheableViewIds: string[] } },
    ) => {
      if (!cacheableViewIds.includes(overridingCachedView.viewId)) {
        return
      }
      for (const [index, cachedView] of cachedViews.entries()) {
        if (cachedView.viewId === overridingCachedView.viewId) {
          // eslint-disable-next-line no-param-reassign
          cachedViews[index] = { ...cachedView, ...overridingCachedView }
          return
        }
      }
      cachedViews.push(overridingCachedView)
    },
    deleteCachedView: ({ cachedViews }: CachedViewsSlice, { payload: viewId }: { payload: string }) => {
      const index = cachedViews.findIndex((cachedView) => cachedView.viewId === viewId)
      if (index !== -1) {
        cachedViews.splice(index, 1)
      }
    },
    clearCachedViews: (state: CachedViewsSlice) => {
      // eslint-disable-next-line no-param-reassign
      state.cachedViews = []
    },
  },
  selectors: {
    selectCachedViews: (state: CachedViewsSlice) => state.cachedViews,
  },
})

const { selectCachedViews } = cachedViews.selectors
export const { _upsertCachedView, deleteCachedView, clearCachedViews } = cachedViews.actions
export default cachedViews.reducer

export const selectCurrentCachedView = createSelector(
  [selectCachedViews, (_, { viewId }: { viewId?: string }) => viewId],
  (cachedViews, viewId) => cachedViews.find((cachedView) => cachedView.viewId === viewId),
)

export const selectUnsavedCachedViews = createSelector([selectCachedViews], (cachedViews: CachedView[]) =>
  cachedViews.filter((cachedView) => {
    if (cachedView.hasChangeToViewSchema === true) {
      return true
    }
    switch (cachedView.config?.type) {
      case 'sheet': {
        return cachedView.changes !== undefined && cachedView.changes.length > 0
      }
      case 'kpiPivot': {
        return cachedView.hasChangeToKpiPivotParameter === true
      }
      case 'kpi': {
        return cachedView.isModifiedKpiForm === true
      }
      default: {
        return false
      }
    }
  }),
)

export const selectShouldUseRefetchCache = createSelector(
  [selectCurrentCachedView, (_, { oldestSyncedAt }: { oldestSyncedAt?: string }): string | undefined => oldestSyncedAt],
  (currentCachedView, oldestSyncedAt) =>
    currentCachedView !== undefined &&
    currentCachedView.shouldIgnoreRefetchCache !== true &&
    ((currentCachedView.oldestSyncedAt !== undefined && oldestSyncedAt === undefined) ||
      currentCachedView.oldestSyncedAt === oldestSyncedAt),
)
